import { lazy, Suspense, useEffect } from 'react'
import { Route, Switch, Redirect, type RouteProps } from 'react-router'
import { observer } from 'mobx-react'
import { useStore } from './stores'
import { NotFoundPage, NotAuthorizedPage } from './Pages/ErrorPage/ErrorPages'
import Header from './Pages/HomePage/Header'
import Footer from './Pages/HomePage/Footer'
import FaqPage from './Pages/FaqPage'
import FosterInfoPage from './Pages/FosterInfoPage'
import InstructionPage from './Pages/InstructionPage'
import InstructionDetailPage from './Pages/InstructionPage/InstructionDetailPage'
import * as Sentry from '@sentry/react'
import Fallback from 'Fallback'
import LoginPage from 'Pages/LoginPage'
import StatusPage from 'Pages/StatusPage'

type ProtectedRouteProps = {
  isAuthorized: boolean
  children
} & RouteProps

const ProtectedRoute = observer(
  ({ isAuthorized, children }: ProtectedRouteProps) => {
    if (isAuthorized) return children

    return (
      <Redirect
        to={{
          pathname: '/not-authorized'
        }}
      />
    )
  }
)

// const LoginPage = lazy(async () => await import('./Pages/LoginPage'))
const HomePage = lazy(async () => await import('./Pages/HomePage'))
const DashboardPage = lazy(async () => await import('./Pages/DashboardPage'))
const MapPage = lazy(async () => await import('./Pages/MapPage'))
const SettingsPage = lazy(async () => await import('./Pages/SettingsPage'))

const App = observer(() => {
  const { user, routing, app: appStore, filter: filterStore } = useStore()

  Sentry.setContext('infos', {
    location: routing.location.pathname,
    project: appStore.selectedProject?.title,
    focusType: appStore.selectedProject !== undefined && appStore.focusType,
    focusIds: appStore.selectedProject !== undefined && appStore.focusIds,
    role: appStore.selectedProject !== undefined && appStore.userRole
  })

  useEffect(() => {
    document.title =
      'ENEKA | ' +
      (process.env.REACT_APP_DEMO_STATUS === 'true' ? 'Demo' : 'Toolbox') +
      (appStore.selectedProject?.title ?? '')
  }, [appStore.selectedProject])

  // we first try to login the user automatically
  if (user.currentUser === undefined) return <Fallback />

  // if it failed we send the user to the login page
  if (user.currentUser === null) return <LoginPage />

  //  now we wait for the rest of the stores to be initialized
  if (!appStore.storesInitialized) return <Fallback />

  return (
    <Suspense fallback={<Fallback />}>
      {filterStore !== undefined && (
        <Route
          component={Header}
          path={['/', '/home', '/map', '/map/:useCase', '/dashboard']}
          exact
        />
      )}
      {user.currentUser !== undefined && (
        <Switch>
          <Route component={FaqPage} path={'/faq'} exact />
          <Route component={FosterInfoPage} path={'/förderinfo'} exact />
          <Route component={InstructionPage} path={'/anleitung'} exact />
          <Route
            component={InstructionDetailPage}
            path={'/anleitung/:slug'}
            exact
          />
          <Route component={HomePage} path={['/', '/home']} exact />
          <Route component={MapPage} path="/map" exact />
          <Route component={DashboardPage} path="/dashboard" exact />
          <Route component={StatusPage} path="/status" exact />

          <Route path="/map/:useCase" exact>
            <ProtectedRoute isAuthorized={appStore.userCanAccessReports}>
              <Route component={MapPage} />
            </ProtectedRoute>
          </Route>

          <Route path="/settings" exact>
            <ProtectedRoute isAuthorized={appStore.userCanEditProjectSettings}>
              <Route component={SettingsPage} />
            </ProtectedRoute>
          </Route>

          <Route component={NotAuthorizedPage} path="/not-authorized" exact />
          <Route component={NotFoundPage} />
        </Switch>
      )}
      <Route component={Footer} path={['/', '/home', '/dashboard']} exact />
    </Suspense>
  )
})

export default App
